import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, MenuItem, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ErrorBanner from '../../common/ErrorBanner';
import { getVoiceLogs } from '../../../api/backendApi';

const columns = [
  { field: 'timestamp', headerName: 'Timestamp', width: 200 },
  { field: 'tenantId', headerName: 'Tenant ID', width: 200 },
  { field: 'purpose', headerName: 'Purpose', width: 160 },
  { field: 'from', headerName: 'From', width: 160 },
  { field: 'to', headerName: 'To', width: 160 },
  { field: 'status', headerName: 'Status', width: 140 },
  { field: 'errorCode', headerName: 'Error Code', width: 140 },
];

const VoiceLogsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await getVoiceLogs(tenantId || undefined);
      const shaped = (Array.isArray(data) ? data : []).map((x, idx) => ({
        id: x.id || idx,
        timestamp: x.timestamp || '',
        tenantId: x.tenantId || '',
        purpose: x.purpose || '',
        from: x.e164_from || x.from || '',
        to: x.e164_to || x.to || '',
        status: x.status || '',
        errorCode: x.errorCode || '',
      }));
      setRows(shaped);
    } catch (e) {
      setError(e?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    const t = setInterval(load, 15000); // 15s
    load();
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    const s = status.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r => String(r.status || '').toLowerCase().includes(s));
  }, [rows, status]);

  return (
    <Box>
      <ErrorBanner message={error} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            label="Filter by Tenant ID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="tenantId"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            select
            label="Filter by Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="in-progress">in-progress</MenuItem>
            <MenuItem value="queued">queued</MenuItem>
            <MenuItem value="ringing">ringing</MenuItem>
            <MenuItem value="failed">failed</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <div style={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </div>
    </Box>
  );
};

export default VoiceLogsTab;
